/**
 * برآورد، به‌شکل یک پیام گفت‌وگو.
 *
 * ⚠️ **رنگ هرگز تنها حامل معنا نیست.** متن حکم همیشه نوشته می‌شود، پس فهم پاسخ
 * به تشخیص رنگ وابسته نیست.
 *
 * چیدمان لایه‌لایه است: پاسخ مستقیم و حکم همیشه دیده می‌شوند، و استدلال، شرط‌ها،
 * ریسک‌ها و شواهد پشت بخش‌های بازشو می‌مانند. کاربر هیئت‌مدیره اول می‌خواهد بداند
 * «چه بود»؛ «چرا» را وقتی خواست باز می‌کند.
 */

import {
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  ChevronDown,
  CircleHelp,
  CircleMinus,
  CirclePlus,
  CircleSlash,
  Crosshair,
  Download,
  FileJson,
  Landmark,
  Lightbulb,
  ListChecks,
  Milestone,
  Quote,
  RefreshCw,
  ShieldAlert,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Badge, Bullets, Ring } from '@/components/ui/primitives'
import { downloadConsultation } from '@/lib/api/endpoints'
import { cn } from '@/lib/cn'
import { fa, faDateTime, saveBlob, truncate } from '@/lib/format'
import { labelOf, verdictHeadline, VERDICT_TONE } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import { CITATION_KIND_LABELS, resolveCitations } from '../citations'

import type {
  ConsultationState,
  UserFeedback,
  Verdict,
  VerdictTransition,
} from '@/lib/types'

const ICONS = {
  support: CirclePlus,
  oppose: CircleMinus,
  conditional: CircleSlash,
  insufficient_information: CircleHelp,
} as const

const TEXTS = {
  support: 'text-support',
  oppose: 'text-oppose',
  conditional: 'text-conditional',
  neutral: 'text-ink-muted',
} as const

const CHIPS = {
  support: 'bg-support-soft text-support ring-support/25',
  oppose: 'bg-oppose-soft text-oppose ring-oppose/25',
  conditional: 'bg-conditional-soft text-conditional ring-conditional/30',
  neutral: 'bg-surface-sunken text-ink-muted ring-line',
} as const

const GLOWS = {
  support: 'bg-support/18',
  oppose: 'bg-oppose/18',
  conditional: 'bg-conditional/20',
  neutral: 'bg-neutral/15',
} as const

interface VerdictMessageProps {
  state: ConsultationState
  onFeedback: (feedback: UserFeedback, correction: string) => void
  /** آخرین برآورد رشته؟ فقط روی آن بازخورد و خروجی معنا دارد. */
  isLatest: boolean
}

export const VerdictMessage = ({ state, onFeedback, isLatest }: VerdictMessageProps) => {
  const { meta } = useMeta()
  const advice = state.advice
  if (advice === null) {
    return null
  }

  const verdict: Verdict = advice.verdict
  const tone = VERDICT_TONE[verdict]
  const Icon = ICONS[verdict]
  const citations = resolveCitations(advice.source_citations, state.supplementary)

  return (
    <div className="relative overflow-hidden rounded-lg rounded-ss-sm border border-line bg-surface-raised shadow-lifted">
      <span
        className={cn(
          'pointer-events-none absolute -top-24 -end-16 size-64 rounded-full blur-3xl',
          GLOWS[tone],
        )}
        aria-hidden
      />

      {/* ---------------- حکم ---------------- */}
      <div className="relative flex flex-wrap items-center justify-between gap-5 p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            className={cn(
              'grid size-11 shrink-0 place-items-center rounded-md ring-1 ring-inset',
              CHIPS[tone],
            )}
            aria-hidden
          >
            <Icon className="size-5.5" />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-ink-muted">تصمیم محتمل</p>
            <p className={cn('text-xl leading-tight font-bold tracking-tight', TEXTS[tone])}>
              {verdictHeadline(meta.labels, verdict, advice.conditional_direction)}
            </p>
          </div>
        </div>
        <Ring value={advice.confidence} label="اطمینان" size={76} />
      </div>

      {/* ---------------- پاسخ مستقیم ---------------- */}
      <div className="relative border-t border-line p-5">
        {/*
          بازقالب‌بندی پیش از پاسخ می‌آید، نه در جزئیات بازشو: اگر صورت مسئله
          عوض شده، خواننده باید همان اول بداند پاسخ به چه چیزی داده شده است.
        */}
        {advice.problem_reframing !== '' && (
          <p className="mb-4 flex gap-2.5 rounded-md border border-line bg-surface-sunken p-3.5 text-sm leading-relaxed text-ink-muted">
            <Crosshair className="mt-0.5 size-4 shrink-0 text-ink-faint" aria-hidden />
            <span>
              <span className="font-semibold text-ink">صورت مسئله: </span>
              {advice.problem_reframing}
            </span>
          </p>
        )}
        <p className="text-[0.9375rem] leading-relaxed font-medium text-ink">
          {advice.direct_answer}
        </p>
        {/*
          ⚠️ «چه کار کنید موافقم» و «چه کار کنید مخالفم» در جزئیات بازشو
          نمی‌نشینند. بازخورد کاربران این بود که خروجی فقط موضع می‌دهد؛ اگر راهِ
          رسیدن به موافقت پشت یک آکاردئون پنهان شود، همان شکایت باقی می‌ماند.
        */}
        {(advice.agreement_conditions.length > 0 ||
          advice.opposition_triggers.length > 0) && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {advice.agreement_conditions.length > 0 && (
              <div className="rounded-md border border-line bg-surface-sunken p-3.5">
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink">
                  <ThumbsUp className="size-3.5 text-emerald-600" aria-hidden />
                  چه کار کنید موافقم
                </p>
                <Bullets items={advice.agreement_conditions} />
              </div>
            )}
            {advice.opposition_triggers.length > 0 && (
              <div className="rounded-md border border-line bg-surface-sunken p-3.5">
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink">
                  <ThumbsDown className="size-3.5 text-rose-600" aria-hidden />
                  چه کار کنید مخالفم
                </p>
                <Bullets items={advice.opposition_triggers} />
              </div>
            )}
          </div>
        )}

        {/* مانع بنیادی از شرط جدا می‌ماند: تلاش برای برآورده کردنش بی‌فایده
            است و کاربر باید همان اول بداند. */}
        {advice.fundamental_objection !== '' && (
          <p className="mt-4 flex gap-2.5 rounded-md border border-line-strong bg-surface-sunken p-3.5 text-sm leading-relaxed text-ink">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-rose-600" aria-hidden />
            <span>
              <span className="font-semibold">مانع بنیادی: </span>
              {advice.fundamental_objection}
            </span>
          </p>
        )}

        {/* تفاوت با دور قبل، بلافاصله پس از پاسخ: در بازسازی نخستین پرسش
            کاربر همین است. */}
        {state.verdict_transition !== null &&
          state.verdict_transition.previous_verdict !== null && (
            <ChangeSummary
              transition={state.verdict_transition}
              total={state.condition_ledger.conditions.length}
            />
          )}

        {advice.recommended_next_action !== '' && (
          <p className="mt-4 flex gap-2.5 rounded-md border border-accent-line bg-accent-soft/60 p-3.5 text-sm leading-relaxed text-ink">
            <ArrowRight className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
            <span>
              <span className="font-semibold">اقدام بعدی: </span>
              {advice.recommended_next_action}
            </span>
          </p>
        )}
      </div>

      {/* ---------------- جزئیات بازشو ---------------- */}
      <div className="relative divide-y divide-line border-t border-line">
        <Section
          title={`برآورد منطق تصمیم ${meta.profile.principal_name}`}
          icon={<Landmark className="size-3.5" aria-hidden />}
        >
          <p className="text-sm leading-relaxed text-ink">{advice.likely_principal_view}</p>
        </Section>

        <Section
          title="استدلال و شرط‌ها"
          icon={<ListChecks className="size-3.5" aria-hidden />}
          count={
            advice.reasoning.length +
            advice.key_conditions.length +
            advice.staged_path.length
          }
        >
          <div className="space-y-5">
            <Bullets title="استدلال" items={advice.reasoning} />
            <Bullets title="شرط‌های کلیدی" items={advice.key_conditions} />
            {/* شرط‌ها هم‌زمان‌اند و مراحل ترتیبی؛ کنار هم بودنشان تفاوت را
                روشن می‌کند. */}
            <Bullets
              title="مسیر مرحله‌ای"
              items={advice.staged_path}
              icon={<Milestone className="size-3 text-ink-faint" aria-hidden />}
            />
          </div>
        </Section>

        <Section
          title="ریسک‌ها و فرض‌ها"
          icon={<AlertTriangle className="size-3.5" aria-hidden />}
          count={advice.risks.length + advice.assumptions.length}
        >
          <div className="space-y-5">
            <Bullets title="ریسک‌ها" items={advice.risks} />
            <Bullets title="فرض‌ها" items={advice.assumptions} />
            <Bullets
              title="چه چیزی می‌تواند پاسخ را عوض کند"
              items={advice.what_could_change_the_answer}
              icon={<RefreshCw className="size-3 text-ink-faint" aria-hidden />}
            />
          </div>
        </Section>

        {/* پرسش تعیین‌کننده بخش خودش را دارد و در فهرست پرسش‌ها گم نمی‌شود:
            وزنش با آن‌ها یکی نیست. */}
        {advice.decisive_question !== '' && (
          <Section
            title="پرسش تعیین‌کننده"
            icon={<CircleHelp className="size-3.5" aria-hidden />}
          >
            <p className="text-sm leading-relaxed font-medium text-ink">
              {advice.decisive_question}
            </p>
          </Section>
        )}

        <Section
          title="نمونه‌های تاریخی"
          icon={<Quote className="size-3.5" aria-hidden />}
          count={advice.historical_examples.length}
        >
          <ul className="space-y-3">
            {advice.historical_examples.map((example) => (
              <li key={example.decision_id} className="rounded-md bg-surface-sunken p-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="neutral" className="tabular">
                    {example.decision_id}
                  </Badge>
                  <p className="min-w-0 flex-1 text-sm font-medium text-ink">
                    {truncate(example.summary, 70)}
                  </p>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
                  {example.relevance}
                </p>
              </li>
            ))}
          </ul>
        </Section>

        {/*
          ارجاع‌ها پیش از این فقط در نمای آزمایشگاه استودیو دیده می‌شدند. یعنی
          مشتری — همان کسی که باید به پاسخ اعتماد کند — پشتوانه‌ی ادعاها را
          نمی‌دید. اعتماد بدون امکان بررسی، اعتماد نیست.
        */}
        <Section
          title="ارجاع به شواهد"
          icon={<Quote className="size-3.5" aria-hidden />}
          count={citations.length}
        >
          <ul className="space-y-3">
            {citations.map((citation) => (
              <li
                key={`${citation.itemId}-${citation.usedFor}`}
                className="rounded-md bg-surface-sunken p-3.5"
              >
                <p className="text-sm font-medium text-ink">{citation.usedFor}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
                  {citation.text}
                </p>
                {citation.kind !== null && (
                  <Badge tone="neutral" className="mt-2">
                    {CITATION_KIND_LABELS[citation.kind]}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </Section>

        {/*
          شاهد مخالف کنار اطمینان می‌نشیند چون هر دو یک چیز می‌گویند: این برآورد
          کجا می‌لنگد. پنهان‌کردنش در جای دور، همان بی‌صدا نادیده‌گرفتن است.
        */}
        <Section
          title="اتکای برآورد"
          icon={<Lightbulb className="size-3.5" aria-hidden />}
        >
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-ink-muted">
              {advice.confidence_explanation}
            </p>
            {advice.counter_evidence !== '' && (
              <div>
                <p className="mb-1.5 text-xs font-semibold text-ink">
                  شاهد مخالف موضع
                </p>
                <p className="text-sm leading-relaxed text-ink-muted">
                  {advice.counter_evidence}
                </p>
              </div>
            )}
            {advice.scale_assessment !== '' && (
              <div>
                <p className="mb-1.5 text-xs font-semibold text-ink">مرتبه‌ی مبلغ</p>
                <p className="text-sm leading-relaxed text-ink-muted">
                  {advice.scale_assessment}
                </p>
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* ---------------- بازخورد و خروجی ---------------- */}
      <footer className="relative flex flex-wrap items-center justify-between gap-3 border-t border-line bg-surface-sunken/60 px-5 py-3">
        <p className="text-[11px] text-ink-faint">
          {state.attempt > 1 && <>تلاش {fa(state.attempt)} · </>}
          {faDateTime(state.started_at)}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {isLatest && <FeedbackButtons onFeedback={onFeedback} />}
          <ExportButtons consultationId={state.consultation_id} />
        </div>
      </footer>
    </div>
  )
}

// ===================================================================
// تفاوت با دور قبل
// ===================================================================

/**
 * خلاصه‌ی تغییر نسبت به بررسی قبلی.
 *
 * ⚠️ حتی وقتی موضع عوض نشده، این خلاصه لازم است: حکمِ ثابتی که می‌گوید چه چیزی
 * بسته شد و چه چیزی باقی است، از حکمِ بی‌اعتنا قابل تشخیص است. نبودِ همین،
 * بازخورد «انگار تأثیری روی نظرش نداشتم» را می‌ساخت.
 */
const ChangeSummary = ({
  transition,
  total,
}: {
  transition: VerdictTransition
  total: number
}) => {
  const { meta } = useMeta()
  if (transition.previous_verdict === null || transition.current_verdict === null) {
    return null
  }
  const previousLabel = verdictHeadline(
    meta.labels,
    transition.previous_verdict,
    transition.previous_direction,
  )
  const currentLabel = verdictHeadline(
    meta.labels,
    transition.current_verdict,
    transition.current_direction,
  )
  return (
    <div className="mt-4 space-y-2 rounded-md border border-line bg-surface-sunken p-3.5">
      <p className="flex items-center gap-2 text-xs font-semibold text-ink">
        <ArrowLeftRight className="size-3.5 text-ink-faint" aria-hidden />
        {transition.changed
          ? `موضع از «${previousLabel}» به «${currentLabel}» رسید`
          : `موضع همان «${currentLabel}» ماند`}
      </p>
      {total > 0 && (
        <p className="text-sm leading-relaxed text-ink-muted">
          از {fa(total)} شرط، {fa(transition.resolved_conditions.length)} اصلاح شده و{' '}
          {fa(transition.remaining_conditions.length)} باقی مانده.
        </p>
      )}
      {transition.reason !== '' && (
        <p className="text-sm leading-relaxed text-ink">{transition.reason}</p>
      )}
      {transition.remaining_conditions.length > 0 && (
        <Bullets title="باقی مانده" items={transition.remaining_conditions} />
      )}
      {transition.stable && (
        <p className="text-xs leading-relaxed text-ink-muted">
          این دور مبنای تازه‌ای نداشت. تکرار همان توصیف نتیجه را عوض نمی‌کند؛ شرط
          باقی‌مانده را با سند و عدد اثبات کنید یا ساختار تصمیم را عوض کنید.
        </p>
      )}
    </div>
  )
}

// ===================================================================
// بخش بازشو
// ===================================================================

const Section = ({
  title,
  icon,
  count,
  children,
}: {
  title: string
  icon: React.ReactNode
  count?: number
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  // بخش خالی رندر نمی‌شود، تا کاربر بخش تهی باز نکند.
  if (count === 0) {
    return null
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value)
        }}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-5 py-3 text-start transition-colors hover:bg-surface-sunken/60"
      >
        <span className="text-accent">{icon}</span>
        <span className="min-w-0 flex-1 text-xs font-semibold text-ink">{title}</span>
        {count !== undefined && (
          <span className="text-[11px] tabular-nums text-ink-faint">{fa(count)}</span>
        )}
        <ChevronDown
          className={cn(
            'size-4 text-ink-faint transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {open && <div className="animate-fade px-5 pt-1 pb-5">{children}</div>}
    </div>
  )
}

// ===================================================================
// بازخورد
// ===================================================================

const FeedbackButtons = ({
  onFeedback,
}: {
  onFeedback: (feedback: UserFeedback, correction: string) => void
}) => {
  const { meta } = useMeta()
  const [sent, setSent] = useState<UserFeedback | null>(null)

  const send = (feedback: UserFeedback): void => {
    setSent(feedback)
    onFeedback(feedback, '')
  }

  return (
    <>
      <Button
        size="sm"
        variant={sent === 'close' ? 'primary' : 'ghost'}
        icon={<ThumbsUp className="size-3.5" aria-hidden />}
        title={labelOf(meta.labels, 'feedback', 'close')}
        onClick={() => {
          send('close')
        }}
      >
        <span className="hidden sm:inline">{labelOf(meta.labels, 'feedback', 'close')}</span>
      </Button>
      <Button
        size="sm"
        variant={sent === 'not_close' ? 'danger' : 'ghost'}
        icon={<ThumbsDown className="size-3.5" aria-hidden />}
        title={labelOf(meta.labels, 'feedback', 'not_close')}
        onClick={() => {
          send('not_close')
        }}
      >
        <span className="hidden sm:inline">
          {labelOf(meta.labels, 'feedback', 'not_close')}
        </span>
      </Button>
    </>
  )
}

// ===================================================================
// خروجی
// ===================================================================

const ExportButtons = ({ consultationId }: { consultationId: string }) => {
  const [busy, setBusy] = useState<'markdown' | 'json' | null>(null)

  const save = async (format: 'markdown' | 'json'): Promise<void> => {
    setBusy(format)
    try {
      const { blob, filename } = await downloadConsultation(consultationId, format)
      saveBlob(blob, filename)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'دریافت خروجی انجام نشد.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        title="خروجی Markdown"
        aria-label="خروجی Markdown"
        loading={busy === 'markdown'}
        icon={<Download className="size-3.5" aria-hidden />}
        onClick={() => {
          void save('markdown')
        }}
      />
      <Button
        size="sm"
        variant="ghost"
        title="خروجی JSON"
        aria-label="خروجی JSON"
        loading={busy === 'json'}
        icon={<FileJson className="size-3.5" aria-hidden />}
        onClick={() => {
          void save('json')
        }}
      />
    </>
  )
}
